import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getCountryFlagById } from '../../util/getCountryFlag';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { onCheckTour } from '../../util/onCheckTour';
import { languagesHelper as language } from '../../util/languages';
import { types as sdkTypes } from '../../util/sdkLoader';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  AddToFavouritesButton,
  IconButtonArrow,
  IconLandscapes,
  ReviewRatingCustom,
} from '../../components';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  if (!Array.isArray(priceVariants) || priceVariants.length === 0) {
    return null;
  }
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const getPriceForDisplay = (price, priceVariants, marketplaceCurrency) => {
  const cheapestVariant = getCheapestPriceVariant(priceVariants);
  if (cheapestVariant?.priceInSubunits != null && marketplaceCurrency) {
    return new Money(cheapestVariant.priceInSubunits, marketplaceCurrency);
  }
  return price;
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  const isNegotiationRegularListingType =
    listingTypeConfig?.listingType === 'negotiation-type-regular';

  if (!isNegotiationRegularListingType && !showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isTour = onCheckTour({ isPriceVariationsInUse, publicData });

  const priceForDisplay = hasMultiplePriceVariants
    ? getPriceForDisplay(price, publicData?.priceVariants, config.currency)
    : price;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(priceForDisplay, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage
        id="ListingCard.perUnit"
        values={{ unitType: !isTour ? publicData?.unitType : 'tour' }}
      />
    </span>
  ) : (
    ''
  );

  const onRequestPrice = (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.priceOnRequest" />
    </span>
  );

  const from = (
    <span className={css.from}>
      {intl.formatMessage({ id: 'ListingCard.from' })}
    </span>
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants || (isNegotiationRegularListingType && price) ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ from, priceValue, pricePerUnit }}
        />
      ) : isNegotiationRegularListingType ? (
        onRequestPrice
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    onDeleteFromFavorites,
    onOpenFafouriteModal,
    isSearchPageCard,
    setExperienceRewildingBg
  } = props;

  const locale = language.get();
  const classes = classNames(rootClassName || css.root, isSearchPageCard && css.rootSearchPage, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { price, publicData } = currentListing.attributes;

  const defaultTitle = currentListing.attributes.title;
  const title = currentListing.attributes.publicData?.[`title_locale_${locale}`] || defaultTitle;

  const slug = createSlug(title);

  const { listingType, cardStyle, linstingFieldId_country, categoryLevel1, field_duration } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const isExperience = categoryLevel1 === "category-tours" || categoryLevel1 === "category-experiences";
  const rating = publicData?.currentRating;
  const field_landscape = publicData?.field_landscape;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  // Handlers for background image on hover
  const handleMouseEnter = () => {
    setExperienceRewildingBg(firstImage);
  };

  const handleMouseLeave = () => {
    setExperienceRewildingBg(null);
  };

  const setActiveBgImage = setExperienceRewildingBg
    ? {
      onMouseEnter: () => handleMouseEnter(),
      // onMouseLeave: () => handleMouseLeave(),
    }
    : null;

  return (
    <div className={classes} {...setActiveBgImage}>
      <NamedLink
        className={css.rootLink}
        name="ListingPage"
        params={{ id, slug }}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className={css.imageHolder}>
          <AddToFavouritesButton
            listing={currentListing}
            onDeleteFromFavorites={onDeleteFromFavorites}
            className={css.addToFavourites}
            onOpenFafouriteModal={onOpenFafouriteModal}
          />
          {!!linstingFieldId_country && <span className={css.flag}>
            {getCountryFlagById(linstingFieldId_country)}
            {language.labelsTranslator(linstingFieldId_country, intl)}
          </span>}
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            currentListing={currentListing}
            config={config}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            style={cardStyle}
            showListingImage={showListingImage}
          />

          {field_landscape && field_landscape !== "not-active-in-my-area" && (
            <div className={classNames(css.landscape, {[css.landscapeWide]: field_landscape === "no-but-i-am-a-member-of-ERN"})}>
              <IconLandscapes type={field_landscape} />
            </div>
          )}
        </div>
        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.category}>
              {language.labelsTranslator(categoryLevel1, intl)} {isExperience &&
                intl.formatMessage({
                  id: 'ListingCard.experience'
                })}
            </div>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            {/* {field_duration && (
            <div className={css.duration}>
              {field_duration}
            </div>
          )} */}
          </div>

          <div className={css.rating}>
            {rating && (
              <>
                <ReviewRatingCustom rating={rating} className={css.ratingStars} />
                <span className={css.ratingTotal}>
                  {rating.toFixed(1)}
                </span>
              </>
            )}
          </div>

          <div className={css.priceInfo}>
            <PriceMaybe
              price={price}
              publicData={publicData}
              config={config}
              intl={intl}
              listingTypeConfig={foundListingTypeConfig}
              listingType={listingType}
            />

            <span className={css.seeDetails}>
              <IconButtonArrow />
              <FormattedMessage id="ListingCard.seeDetails" />
            </span>

          </div>
        </div>
      </NamedLink>
    </div>
  );
};

export default ListingCard;
